import coreIcon from "./assets/core-icon.svg";
import proIcon from "./assets/pro-icon.png";

export type PlanId = "core" | "pro";
export type CurrencyCode = "USD" | "GBP" | "EUR" | "CAD";
export type BillingCycle = "year" | "month";
export type DealId = "free-device" | "discount";

export const DEFAULT_PLAN: PlanId = "core";
export const DEFAULT_CURRENCY: CurrencyCode = "USD";
export const DEFAULT_BILLING_CYCLE: BillingCycle = "year";

export const CURRENCY_OPTIONS: { label: string; value: CurrencyCode }[] = [
  { label: "USD", value: "USD" },
  { label: "GBP", value: "GBP" },
  { label: "EUR", value: "EUR" },
  { label: "CAD", value: "CAD" },
];

/** All supported currencies — fetched together so changing currency never refetches. */
export const ALL_CURRENCY_CODES: CurrencyCode[] = CURRENCY_OPTIONS.map(
  (option) => option.value,
);

/**
 * Static plan presentation content (icon, copy, features). Prices are NOT here —
 * they come from the billing API via `useOnboardingPlans`. Only the marketing
 * copy is local; swap for catalog metadata if the API exposes it later.
 */
export interface PlanContent {
  name: string;
  icon: string;
  iconAlt: string;
  description: string;
  featureTitle: string;
  features: string[];
}

export const PLAN_CONTENT: Record<PlanId, PlanContent> = {
  core: {
    name: "Core",
    icon: coreIcon,
    iconAlt: "Core plan",
    description: "Best for businesses getting started with digital signage.",
    featureTitle: "Featuring:",
    features: [
      "80+ apps & integrations",
      "Unlimited file storage",
      "Design templates & Quick Post",
    ],
  },
  pro: {
    name: "Pro",
    icon: proIcon,
    iconAlt: "Pro plan",
    description: "Ideal for advanced analytics and content for your business.",
    featureTitle: "Core Features, Plus:",
    features: ["Premium apps & integrations", "Dashboards", "QR code metrics"],
  },
};

/** Discount deal percentage. Confirm with Product whether fixed or derived. */
export const DISCOUNT_PERCENT = 17;

/**
 * Per-device value (in cents) used to compute the free-device deal savings
 */
export const PER_DEVICE_VALUE_CENTS: Record<CurrencyCode, number> = {
  USD: 6500,
  GBP: 5500,
  EUR: 7500,
  CAD: 9500,
};

/**
 * Which deals a selection unlocks. Deals are only offered on Yearly/Annual;
 * Monthly shows the no-deals state. On Yearly both Core and Pro get the
 * free-device and discount deals.
 */
export interface DealEligibility {
  anyDeal: boolean;
  freeDeviceAvailable: boolean;
  discountAvailable: boolean;
}

export const dealEligibility = (cycle: BillingCycle): DealEligibility => {
  if (cycle === "month") {
    return {
      anyDeal: false,
      freeDeviceAvailable: false,
      discountAvailable: false,
    };
  }
  return {
    anyDeal: true,
    freeDeviceAvailable: true,
    discountAvailable: true,
  };
};
